import React, { useState, useEffect, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Button, PageHeader, Typography, Row, Col, message } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import { MyBreadcrumb, renderInfoBlock, shouldShowBlock } from 'utils';
import { rackDeviceGetByIdService } from 'services/rackdevice';
import { portregistryByDeviceService } from 'services/portregistry';
import NetworkToolWrapper from '../../layouts/NetworkToolWrapper';
import RackDeviceVisualization from '../../components/rack-diagram/RackDeviceVisualization';
import { useRackDiagramBreadcrumb } from '../../hooks/useRackDiagramBreadcrumb';
import { useReactToPrint } from 'react-to-print';

const { Title } = Typography;

export default function RackDeviceView() {
  const { id } = useParams();
  const history = useHistory();
  const [device, setDevice] = useState(null);
  const [ports, setPorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const printRef = useRef();

  const breadcrumbItems = useRackDiagramBreadcrumb(
    device?.equipment_name || 'View Device',
    '',
  );

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
  });

  useEffect(() => {
    loadDeviceData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadDeviceData = () => {
    rackDeviceGetByIdService(id)
      .then((response) => {
        const deviceData = response.data.items;
        setDevice(deviceData);

        const deviceregistryId =
          deviceData?.deviceregistry?._id || deviceData?.deviceregistry;
        if (deviceregistryId) {
          portregistryByDeviceService(deviceregistryId)
            .then((portResponse) => {
              setPorts(portResponse.data.items || []);
            })
            .catch(() => setPorts([]));
        } else {
          setPorts([]);
        }
      })
      .catch((error) => {
        message.error('Failed to load rack device details');
        console.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <NetworkToolWrapper isBranchClientRequired={false}>
      <MyBreadcrumb items={breadcrumbItems} />
      <PageHeader
        ghost={false}
        onBack={() => history.push('/rackdevice')}
        title="Rack Device Details"
        extra={[
          <Button
            key="print"
            icon={<PrinterOutlined />}
            onClick={handlePrint}
            style={{ marginRight: 8 }}
          >
            Print
          </Button>,
          <Button
            key="edit"
            type="primary"
            onClick={() => history.push(`/rackdevice/update/${id}`)}
          >
            Edit Device
          </Button>,
        ]}
      >
        <div ref={printRef} className="rack-device-print-container">
          {loading ? (
            <div>Loading...</div>
          ) : device ? (
            <>
              <div className="descriptionView2-heading">
                <Title level={3}>Rack Device - {device?.equipment_name}</Title>
                {device?.status && (
                  <Title level={3}>
                    Status:{' '}
                    <span
                      className={
                        device.status === 'ACTIVE'
                          ? 'text-green'
                          : 'text-yellow'
                      }
                    >
                      {device.status}
                    </span>
                  </Title>
                )}
              </div>

              <RackDeviceVisualization device={device} ports={ports} />

              <div
                className="rack-device-content"
                style={{ maxWidth: 900, margin: 'auto' }}
              >
                <Row gutter={[16, 16]}>
                  {shouldShowBlock({
                    heading: 'General Information',
                    source: device,
                    items: [
                      'equipment_name',
                      {
                        key: 'Vendor',
                        value: device?.vendor?.name || '',
                      },
                      {
                        key: 'Model',
                        value: device?.model?.name || '',
                      },
                      {
                        key: 'Device Registry',
                        value: device?.deviceregistry?.name || '',
                      },
                      'lock_code',
                      'labeled_as',
                      'serial_number',
                      'service_tag',
                      'status',
                      'reuse_option',
                      'primary_use',
                    ],
                  }) && (
                    <Col xs={24} xl={12}>
                      {renderInfoBlock({
                        heading: 'General Information',
                        source: device,
                        items: [
                          'equipment_name',
                          {
                            key: 'Vendor',
                            value: device?.vendor?.name || '',
                          },
                          {
                            key: 'Model',
                            value: device?.model?.name || '',
                          },
                          {
                            key: 'Device Registry',
                            value: device?.deviceregistry?.name || '',
                          },
                          'lock_code',
                          'labeled_as',
                          'serial_number',
                          'service_tag',
                          'status',
                          'reuse_option',
                          'primary_use',
                        ],
                      })}
                    </Col>
                  )}

                  {shouldShowBlock({
                    heading: 'Physical Details',
                    source: device,
                    items: [
                      'equipment_alert',
                      'number_of_u',
                      'total_psus',
                      'total_nics',
                      'nic_facing',
                      'equipment_notes',
                    ],
                  }) && (
                    <Col xs={24} xl={12}>
                      {renderInfoBlock({
                        heading: 'Physical Details',
                        source: {
                          ...device,
                          number_of_u: device.number_of_u
                            ? `${device.number_of_u}U`
                            : '',
                        },
                        items: [
                          'equipment_alert',
                          'number_of_u',
                          'total_psus',
                          'total_nics',
                          'nic_facing',
                          'equipment_notes',
                        ],
                      })}
                    </Col>
                  )}
                </Row>

                {device?.psu_details && device.psu_details.length > 0 && (
                  <div style={{ marginTop: 16 }}>
                    <Row gutter={[16, 16]}>
                      {device.psu_details.map((psu, index) => (
                        <Col xs={24} xl={12} key={index}>
                          {renderInfoBlock({
                            heading: `PSU ${index + 1}`,
                            source: psu,
                            items: ['serial_number', 'notes'],
                          })}
                        </Col>
                      ))}
                    </Row>
                  </div>
                )}

                {device?.nic_details && device.nic_details.length > 0 && (
                  <div style={{ marginTop: 16 }}>
                    <Row gutter={[16, 16]}>
                      {device.nic_details.map((nic, index) => (
                        <Col xs={24} xl={12} key={nic._id || index}>
                          {renderInfoBlock({
                            heading: `NIC ${index + 1}`,
                            source: nic,
                            items: [
                              'serial_number',
                              'port_type',
                              'speed',
                              'notes',
                            ],
                          })}
                        </Col>
                      ))}
                    </Row>
                  </div>
                )}

                {shouldShowBlock({
                  heading: 'Metadata',
                  source: device,
                  items: [
                    {
                      key: 'Created By',
                      value:
                        device?.created_by?.display_name ||
                        device?.created_by?.email ||
                        '',
                    },
                    {
                      key: 'Created At',
                      value: device?.created_at
                        ? new Date(device.created_at).toLocaleString()
                        : '',
                    },
                    {
                      key: 'Last Updated',
                      value: device?.updated_at
                        ? new Date(device.updated_at).toLocaleString()
                        : '',
                    },
                    {
                      key: 'Last Updated By',
                      value:
                        device?.updated_by?.display_name ||
                        device?.updated_by?.email ||
                        '',
                    },
                  ],
                }) && (
                  <div style={{ marginTop: 16 }}>
                    <Row gutter={[16, 16]}>
                      <Col xs={24} xl={12}>
                        {renderInfoBlock({
                          heading: 'Metadata',
                          source: device,
                          items: [
                            {
                              key: 'Created By',
                              value:
                                device?.created_by?.display_name ||
                                device?.created_by?.email ||
                                '',
                            },
                            {
                              key: 'Created At',
                              value: device?.created_at
                                ? new Date(device.created_at).toLocaleString()
                                : '',
                            },
                            {
                              key: 'Last Updated',
                              value: device?.updated_at
                                ? new Date(device.updated_at).toLocaleString()
                                : '',
                            },
                            {
                              key: 'Last Updated By',
                              value:
                                device?.updated_by?.display_name ||
                                device?.updated_by?.email ||
                                '',
                            },
                          ],
                        })}
                      </Col>
                    </Row>
                  </div>
                )}
              </div>
            </>
          ) : null}
        </div>
      </PageHeader>
    </NetworkToolWrapper>
  );
}
